import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import { Article } from '../types';
import MapComponent from '../components/MapComponent';

const Maps: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [hoveredArticle, setHoveredArticle] = useState<Article | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // Fetch articles that have location data
        const articlesData = await ApiService.getArticles({ limit: 100 });
        const articlesWithLocation = articlesData.filter(article => 
          article.location_lat && article.location_lng && article.location_name
        );
        setArticles(articlesWithLocation);
        
        // Set the first article with location as selected by default
        if (articlesWithLocation.length > 0) {
          setSelectedArticle(articlesWithLocation[0]);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const handleLocationSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Filter articles by location name or title
      const filtered = articles.filter(article => 
        article.location_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filtered.length > 0) {
        setSelectedArticle(filtered[0]);
      }
    }
  };

  const handleArticleHover = (article: Article | null) => {
    setHoveredArticle(article);
  };

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
  };

  const handleArticleNavigate = (article: Article) => {
    navigate(`/article/${article.slug}`);
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="bg-[#fbfaf8] text-[#1b140e] min-h-screen">
      <main className="flex-1 px-[10%] py-[5%]">
        <div className="mx-auto max-w-full h-full">
          <div className="mb-4">
            <h2 className="text-4xl font-bold text-[#1b140e] tracking-tight">Maps</h2>
            <p className="mt-2 text-lg text-[#6b5e52]">Explore articles by location.</p>
          </div>

          <div className="relative h-[calc(100vh-240px)] rounded-lg shadow-lg overflow-hidden border border-[#e7e1d9]">
            {/* Search Box */}
            <div className="absolute top-4 left-4 right-4 z-20 sm:max-w-md">
              <form onSubmit={handleLocationSearch}>
                <label className="relative">
                  <input 
                    className="form-input w-full rounded-md border-0 bg-white/90 py-2.5 px-4 text-base text-[#1b140e] shadow-sm placeholder:text-[#957350] backdrop-blur-sm focus:ring-2 focus:ring-inset focus:ring-[#c9731d]"
                    placeholder="Search locations or articles..." 
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </label>
              </form>
            </div>

            {/* Map Component */}
            <MapComponent
              articles={articles}
              onArticleHover={handleArticleHover}
              onArticleClick={handleArticleClick}
              onArticleNavigate={handleArticleNavigate}
              selectedArticle={selectedArticle}
            />

            {/* Article Preview Panel */}
            {hoveredArticle && (
              <div className="absolute bottom-4 left-4 z-20 bg-white/95 rounded-lg p-4 shadow-lg backdrop-blur-sm max-w-sm">
                <h4 className="font-semibold text-lg text-[#1b140e] mb-2">{hoveredArticle.title}</h4>
                <p className="text-sm text-[#6b5e52] mb-2">{hoveredArticle.excerpt.substring(0, 150)}...</p>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-[#957350]">
                    By {hoveredArticle.author}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleArticleNavigate(hoveredArticle)}
                      className="text-xs bg-[var(--primary-color)] text-white px-3 py-1 rounded hover:bg-[#a3886c] transition-colors"
                    >
                      Read Article
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Selected Article Details Panel */}
            {selectedArticle && (
              <div className="absolute bottom-4 right-4 z-20 bg-white/95 rounded-lg p-4 shadow-lg backdrop-blur-sm max-w-sm">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#1b140e] mb-1">
                      {selectedArticle.location_name}
                    </h3>
                    <p className="text-sm text-[#6b5e52]">
                      {articles.filter(a => a.location_name === selectedArticle.location_name).length} article{articles.filter(a => a.location_name === selectedArticle.location_name).length > 1 ? 's' : ''} here
                    </p>
                  </div>
                  <button 
                    className="p-1.5 rounded-full hover:bg-black/10"
                    onClick={() => setSelectedArticle(null)}
                  >
                    <span className="material-symbols-outlined text-[#6b5e52]">close</span>
                  </button>
                </div>
                
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {articles
                    .filter(article => article.location_name === selectedArticle.location_name)
                    .slice(0, 3)
                    .map((article) => (
                    <div 
                      key={article.id}
                      className="p-2 rounded-md hover:bg-black/5 cursor-pointer"
                      onClick={() => handleArticleNavigate(article)}
                    >
                      <h4 className="font-semibold text-sm text-[#1b140e]">{article.title}</h4>
                      <p className="text-xs text-[#6b5e52] mt-1">{article.excerpt.substring(0, 80)}...</p>
                      <p className="text-xs text-[#957350] mt-1">
                        By {article.author}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <p className="mt-2 text-center text-xs text-[#957350]">
            Map data © OpenStreetMap contributors
          </p>
        </div>
      </main>
    </div>
  );
};

export default Maps;